/**
 * @file: BubbleReactions.tsx
 *
 * The reaction chips under a message bubble — one chip per emoji with its
 * count, the viewer's own reactions tinted. Tapping a chip toggles that emoji
 * (add if not yet reacted, remove if already). Pure: the container supplies
 * the summaries + the toggle handler.
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessageReactionSummary } from '@euler/model/chat';
import { FC, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const BubbleReactions: FC<{
  reactions: ChatMessageReactionSummary[];
  isMine: boolean;
  onToggle: (emoji: string) => void;
}> = memo(({ reactions, isMine, onToggle }) => {
  const { rpx } = useResponsiveContext();
  if (reactions.length === 0) return null;
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: rpx(4),
        marginTop: rpx(4),
        alignSelf: isMine ? 'flex-end' : 'flex-start',
        justifyContent: isMine ? 'flex-end' : 'flex-start',
      }}
    >
      {reactions.map(r => (
        <TouchableOpacity
          key={r.emoji}
          activeOpacity={0.7}
          onPress={() => onToggle(r.emoji)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: rpx(8),
            paddingVertical: rpx(3),
            borderRadius: rpx(12),
            borderWidth: 1,
            backgroundColor: r.reactedByMe ? '#dbeafe' : '#eceef2',
            borderColor: r.reactedByMe ? '#3b82f6' : '#dcdfe5',
          }}
        >
          <Typography.Text size={rpx(13)} lineHeight={rpx(18)}>
            {r.emoji}
          </Typography.Text>
          <Typography.Text
            size={rpx(11)}
            lineHeight={rpx(18)}
            color={r.reactedByMe ? '#2563EB' : '#6b7280'}
            style={{
              marginLeft: rpx(3),
              fontFamily: FontFamily.NotoSans.Medium,
            }}
          >
            {r.count}
          </Typography.Text>
        </TouchableOpacity>
      ))}
    </View>
  );
});
